import {Feature, startModalLoading, stopModalLoading} from '../reducers/environmentReducer';
import {FeatureFlag} from '../../../@type/features';
import {addAction} from '../../../@shared/ts/hooks';
import {captureSentryException} from '../../../@shared/ts/sentry';
import {$qs, $qsAll} from '../../../@shared/ts/dom';
import {store} from '../store';
import {requestCartCalculation} from '../payment/order';
import {addProductToCart} from '../util/cart';
import spinnerImageURL from '../../img/spinner.svg';

type UpsellFlow = 'pp_button' | 'before_payment';
type UpsellProduct = {
	'id': string;
	'name': string;
	'price': string;
	'image': string;
};

export function initOneClickUpsell() {
	if (!Feature.enabled(FeatureFlag.ONE_CLICK_UPSELL)) {
		return;
	}

	const upsellFlow = Feature.metadata<UpsellFlow>(FeatureFlag.ONE_CLICK_UPSELL, 'pp_ocu_flow');
	if (!upsellFlow) {
		throw new Error('Invalid OCU flow action. Expected a non empty string. Received: ' + String(upsellFlow));
	}

	const shownProducts: string[] = [];
	const oneClickUpsell = async () => {
		try {
			const upsellProduct = Feature.dynamicMetadata<UpsellProduct>(FeatureFlag.ONE_CLICK_UPSELL, 'pp_ocu_products');
			if (!upsellProduct) {
				throw new Error(`Invalid OCU product. Expected an object. Received: ${String(upsellProduct)}`);
			}

			if (shownProducts.includes(upsellProduct.id)) {
				return;
			}

			shownProducts.push(upsellProduct.id);

			store.dispatch(stopModalLoading());
			await promptOneClickUpsell(upsellProduct);
			store.dispatch(startModalLoading());
		} catch (err: unknown) {
			if (err instanceof Error) {
				console.error('Handled error:', err);
				captureSentryException(err);
			}
		}
	};

	if (upsellFlow === 'pp_button') {
		addAction('after_modal_open', oneClickUpsell);
	} else if (upsellFlow === 'before_payment') {
		addAction('after_payment_page', oneClickUpsell);
	}
}

async function promptOneClickUpsell(upsellProduct: UpsellProduct): Promise<void> {
	const headline = Feature.metadata<string>(FeatureFlag.ONE_CLICK_UPSELL, 'headline_text');
	if (!headline) {
		throw new Error(`Invalid OCU headline text. Expected a non empty string. Received: ${String(headline)}`);
	}

	const subHeadline = Feature.metadata<string>(FeatureFlag.ONE_CLICK_UPSELL, 'sub_headline_text');
	if (subHeadline && typeof subHeadline !== 'string') {
		throw new Error(`Invalid OCU sub headline text. Expected a non empty string. Received: ${String(subHeadline)}`);
	}

	const acceptText = Feature.metadata<string>(FeatureFlag.ONE_CLICK_UPSELL, 'accept_button_text');
	if (!acceptText) {
		throw new Error(`Invalid OCU accept button text. Expected a non empty string. Received: ${String(acceptText)}`);
	}

	const declineText = Feature.metadata<string>(FeatureFlag.ONE_CLICK_UPSELL, 'decline_button_text');
	if (!declineText) {
		throw new Error(`Invalid OCU decline button text. Expected a non empty string. Received: ${String(declineText)}`);
	}

	const customDescription = Feature.metadata<string>(FeatureFlag.ONE_CLICK_UPSELL, 'custom_description');
	if (customDescription && typeof customDescription !== 'string') {
		throw new Error(`Invalid OCU custom description. Expected a string. Received: ${String(customDescription)}`);
	}

	return new Promise((resolve, _reject) => {
		const $container = document.createElement('div');
		$container.id = 'pp-ocu-container';

		// eslint-disable-next-line spaced-comment
		$container.innerHTML = /*html*/`
		<div id="pp-ocu-backdrop" data-close-ocu>
			<div id="pp-ocu-body">
				<button class="pp-ocu-x" data-close-ocu>&times;</button>

				<div class="pp-ocu-headline">${headline}</div>
				<div class="pp-ocu-sub-headline ${subHeadline ? '' : 'hide'}">${subHeadline ?? ''}</div>

				<img class="pp-ocu-product-img" src="${upsellProduct.image}">

				<div class="pp-ocu-product-name">${upsellProduct.name}</div>
				<div class="pp-ocu-product-description ${customDescription ? '' : 'hide'}">${customDescription ?? ''}</div>
				
				<div class="pp-ocu-product-price">${upsellProduct.price}</div>

				<button class="pp-ocu-accept-button" data-ocu-product-id="${upsellProduct.id}">${acceptText}</button>
				<button class="pp-ocu-decline-button">${declineText}</button>
			</div>
		</div>`;

		document.body.appendChild($container);

		const destroyPrompt = () => {
			$container.remove();
		};

		// Add listener for closing the OCU page either by clicking the "x"
		// button or by the "Decline" button.
		$qsAll('.pp-ocu-x,.pp-ocu-decline-button', $el => {
			$el.addEventListener('click', e => {
				e.preventDefault();

				destroyPrompt();
				resolve();
			});
		});

		// Event listener for the "Accept" button.
		$qs('.pp-ocu-accept-button')?.addEventListener('click', async e => {
			e.preventDefault();

			const $target = e.target as HTMLButtonElement;
			const productId = Number.parseInt(upsellProduct.id);

			const markup = $target.innerHTML;
			$target.disabled = true;
			$target.innerHTML = `<img src="${spinnerImageURL}" style="height: 1em;"/>`;

			await addProductToCart(productId);
			await requestCartCalculation('push', 'ocu:accept');

			$target.disabled = false;
			$target.innerHTML = markup;

			destroyPrompt();
			resolve();
		});
	});
}
